import { useTranslate } from "@lobby/ocb-intl";
import {
  type MouseEvent,
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useRef,
} from "react";
import { Button } from "../button";
import { Icon } from "../icons";
import { clsx } from "clsx";

interface DialogProps {
  className?: string;
  type: "success" | "warning" | "error";
  closable?: boolean;
  maskClosable?: boolean;
  footer?: ReactNode;
  onClose?: VoidFunction;
}

const buttonVariantMap = {
  success: "success",
  warning: "warning",
  error: "danger",
} as const;

function DialogComponent({
  className,
  children,
  type,
  closable = true,
  maskClosable,
  footer: footerSlot,
  onClose,
}: PropsWithChildren<DialogProps>) {
  const { $t } = useTranslate();

  const dialogRef = useRef<HTMLDialogElement>(null);

  const footer =
    footerSlot === undefined ? (
      <div className="mx-auto mt-5 lg:mt-7 w-fit">
        <Button variant={buttonVariantMap[type]} onClick={handleClose}>
          {$t({ defaultMessage: "ok" })}
        </Button>
      </div>
    ) : (
      footerSlot
    );

  function handleClose() {
    dialogRef.current?.close();
    onClose?.();
  }

  function handleBackdropClick(e: MouseEvent) {
    e.nativeEvent.stopImmediatePropagation();

    if (maskClosable && e.target === e.currentTarget) {
      handleClose();
    }
  }

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  return (
    <dialog
      ref={dialogRef}
      data-type={type}
      onClick={handleBackdropClick}
      className={clsx("dialog-core -animated", className)}
    >
      <div className="dialog-core-inner">
        {closable && (
          <button
            type="button"
            className="absolute top-5 right-5 p-[0.3125rem] text-[1.375rem] hover:text-white text-accent"
            onClick={handleClose}
          >
            <Icon name="close" />
          </button>
        )}
        {children}
        {footer}
      </div>
    </dialog>
  );
}

function Title({ children }: PropsWithChildren) {
  return <div className="dialog-core-title">{children}</div>;
}

function Message({ children }: PropsWithChildren<{ className?: string }>) {
  return (
    <div className="font-medium text-sm lg:text-lg whitespace-pre-wrap">
      {children}
    </div>
  );
}

function Description({ children }: PropsWithChildren) {
  return (
    <div className="mt-3 text-xs lg:mt-4 lg:text-sm whitespace-pre-wrap">
      {children}
    </div>
  );
}

export const Dialog = Object.assign(DialogComponent, {
  Title,
  Message,
  Description,
});
